import { useRef, useState } from 'react'

import type { UserShortType } from '@/types'
import api from '@/utils/api/api'

import { USERS_PAGE_SIZE } from './SubscriptionCreationModal.config'

type UsersCache = {
  users: UserShortType[]
  page: number
  hasMore: boolean
}

function useSubscriptionUsers() {
  const [users, setUsers] = useState<UserShortType[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const pageRef = useRef(0)
  const loadingMoreRef = useRef(false)
  const myOrgCacheRef = useRef<UsersCache | null>(null)

  const fetchUsers = (
    orgCode: number,
    page: number,
    append: boolean,
    isMyOrg: boolean
  ) => {
    if (append) {
      setLoadingMore(true)
      loadingMoreRef.current = true
    } else {
      setLoading(true)
    }
    api
      .get('/users', {
        params: {
          organisationCode: orgCode,
          page,
          pageSize: USERS_PAGE_SIZE,
          telegramActivatedOrderBy: 'DESC',
        },
      })
      .then((response) => {
        const data = response.data.users
        const content: UserShortType[] = data?.content ?? []
        const total: number = data?.totalElements ?? 0
        const nextHasMore = (page + 1) * USERS_PAGE_SIZE < total
        pageRef.current = page
        setUsers((prev) => {
          const result = append ? [...prev, ...content] : content
          if (isMyOrg) {
            myOrgCacheRef.current = {
              users: result,
              page,
              hasMore: nextHasMore,
            }
          }
          return result
        })
        setHasMore(nextHasMore)
      })
      .catch(() => {
        if (!append) setUsers([])
      })
      .finally(() => {
        setLoading(false)
        setLoadingMore(false)
        loadingMoreRef.current = false
      })
  }

  const loadMyOrgUsers = (orgCode?: number) => {
    if (!orgCode) return
    const cache = myOrgCacheRef.current
    if (cache) {
      setUsers(cache.users)
      pageRef.current = cache.page
      setHasMore(cache.hasMore)
      return
    }
    fetchUsers(orgCode, 0, false, true)
  }

  const loadOrgUsers = (orgCode: number) => {
    pageRef.current = 0
    setHasMore(false)
    fetchUsers(orgCode, 0, false, false)
  }

  const loadMoreUsers = (orgCode: number | undefined, isMyOrg: boolean) => {
    if (!orgCode || !hasMore || loadingMoreRef.current) return
    fetchUsers(orgCode, pageRef.current + 1, true, isMyOrg)
  }

  const clearUsers = () => setUsers([])

  return {
    users,
    loading,
    loadingMore,
    loadMyOrgUsers,
    loadOrgUsers,
    loadMoreUsers,
    clearUsers,
  }
}

export default useSubscriptionUsers
